import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import Select from "react-select";

function AddProduct() {
  const [product, setProduct] = useState({
    name: "",
    category: "",
    subcategory: "",
    brand: "",
    price: "",
    mrp: "",
    stock: "",
    sku: "",
    description: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // ✅ Fetch categories
  useEffect(() => {
    axios
      .get(`${baseurl}getCategories`)
      .then((res) => setCategories(res.data.categories || []))
      .catch((err) => console.error("Failed to fetch categories:", err));
  }, []);

  // ✅ Fetch subcategories + brands when category changes
 const handleCategoryChange = async (e) => {
  const categoryId = e.target.value;
  setProduct({ ...product, category: categoryId, subcategory: "", brand: "" });
  setBrands([]);
  setSubcategories([]);

  if (!categoryId) return;

  try {
    // ✅ Fetch both in parallel (no filtering at API level)
    const [subRes, brandRes] = await Promise.all([
      axios.get(`${baseurl}categories/${categoryId}/subcategories`),
      axios.get(`${baseurl}getAllBrands`),
    ]);

    // ✅ Filter brands locally by category_id
    const allBrands = brandRes.data.brands || [];
    const filteredBrands = allBrands.filter(
      (b) => String(b.category_id) === String(categoryId)
    );

    // ✅ Add "Other" brand manually
    const extendedBrands = [
      ...filteredBrands,
      { brand_id: "other", brand_name: "Other" },
    ];

    setSubcategories(subRes.data.subcategories || []);
    setBrands(extendedBrands);
  } catch (err) {
    console.error("Error fetching subcategories or brands:", err);
  }
};


  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct({ ...product, [name]: value });
  };

  const handleSubcategoryChange = (e) => {
    setProduct({ ...product, subcategory: e.target.value });
  };

  const handleBrandSelect = (selectedOption) => {
    setProduct({ ...product, brand: selectedOption ? selectedOption.value : "" });
  };

  const vendorId = localStorage.getItem("vendorId");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...product, vendor_id: vendorId };

      // ✅ Make brand optional — remove field if empty
      if (!payload.brand) delete payload.brand;

      const response = await axios.post(`${baseurl}addVendorProduct`, payload);
      const { uuid } = response.data.product;

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Product added successfully!",
        showConfirmButton: false,
        timer: 2000,
        toast: true,
      });

      setTimeout(() => {
        window.location.href = `/product/gallery/${uuid}`;
      }, 2100);
    } catch (error) {
      console.error("Error sending data to backend:", error);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Failed to save product!",
        showConfirmButton: false,
        timer: 2500,
        toast: true,
      });
    }
  };

  // ✅ React Select brand options
  const brandOptions = brands.map((b) => ({
    value: b.brand_id,
    label: b.brand_name,
  }));

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">Add Product - Step 1</div>
          </div>

          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-header">Basic Product Info</div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Product Name */}
                    <div className="col-md-6 mb-3">
                      <label>Product Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        placeholder="Enter Product Name"
                        value={product.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Category */}
                    <div className="col-md-6 mb-3">
                      <label>Category</label>
                      <select
                        className="form-control"
                        value={product.category}
                        onChange={handleCategoryChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subcategory */}
                    <div className="col-md-6 mb-3">
                      <label>Subcategory</label>
                      <select
                        className="form-control"
                        value={product.subcategory}
                        onChange={handleSubcategoryChange}
                        required
                        disabled={!product.category}
                      >
                        <option value="">Select Subcategory</option>
                        {subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ✅ Brand (Optional React Select) */}
                    <div className="col-md-6 mb-3">
                      <label>Brand (Optional)</label>
                      {brands.length > 0 ? (
                        <Select
                          options={brandOptions}
                          value={
                            brandOptions.find(
                              (opt) => opt.value === product.brand
                            ) || null
                          }
                          onChange={handleBrandSelect}
                          placeholder="Search or select brand..."
                          isClearable
                        />
                      ) : (
                        <p className="text-muted small mt-2">
                          No brands found for this category.
                        </p>
                      )}
                    </div>

                    {/* Price */}
                    <div className="col-md-3 mb-3">
                      <label>Price</label>
                      <input
                        type="number"
                        className="form-control"
                        name="price"
                        placeholder="Enter Price"
                        value={product.price}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* MRP */}
                    <div className="col-md-3 mb-3">
                      <label>MRP</label>
                      <input
                        type="number"
                        className="form-control"
                        name="mrp"
                        placeholder="Enter MRP"
                        value={product.mrp}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Stock */}
                    <div className="col-md-3 mb-3">
                      <label>Stock</label>
                      <input
                        type="number"
                        className="form-control"
                        name="stock"
                        placeholder="Available Quantity"
                        value={product.stock}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* SKU */}
                    <div className="col-md-3 mb-3">
                      <label>SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        name="sku"
                        placeholder="Enter SKU"
                        value={product.sku}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Description */}
                    <div className="col-12 mb-3">
                      <label>Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={product.description}
                        onChange={(event, editor) =>
                          setProduct({ ...product, description: editor.getData() })
                        }
                        onReady={(editor) => {
                          editor.editing.view.change((writer) => {
                            writer.setStyle(
                              "min-height",
                              "300px",
                              editor.editing.view.document.getRoot()
                            );
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-12 mt-2 text-end">
                    <button type="submit" className="btn btn-primary">
                      Next
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default AddProduct;
